import { Plus } from "lucide-mui";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTenants } from "../../../../hooks/resources.ts";
import { api } from "../../../../api/client.ts";
import type { Connection } from "../../../../api/client.ts";
import { keys } from "../../../../api/queryKeys.ts";
import AddCrmConnectionDialog from "./AddCrmConnectionDialog.tsx";
import CrmProviderLogo from "../../../../components/CrmProviderLogo.tsx";
import CrmConnectionList from "./CrmConnectionList.tsx";
import CrmPeopleDialog from "./CrmPeopleDialog.tsx";
import AccordionCard from "../../../../components/AccordionCard.tsx";
import type { Status } from "../../../../components/StatusIcon.tsx";
import TenantMappingsDialog from "./TenantMappingsDialog.tsx";
import UpdateCredentialsDialog from "./UpdateCredentialsDialog.tsx";

export default function CrmIntegrationSection({ organizationId }: { organizationId: string }) {
  const queryClient = useQueryClient();

  const [adding, setAdding] = useState(false);

  const [editingConn, setEditingConn] = useState<Connection | null>(null);
  const [newCredentials, setNewCredentials] = useState<Record<string, string>>({});

  const [mappingConn, setMappingConn] = useState<Connection | null>(null);
  const [peopleConn, setPeopleConn] = useState<Connection | null>(null);
  const [mappingTenantId, setMappingTenantId] = useState("");
  const [mappingPipelineId, setMappingPipelineId] = useState("");
  // Set when a new connection was created but mapping it to its tenant failed.
  const [mappingNotice, setMappingNotice] = useState<string | null>(null);

  const connections = useQuery({
    queryKey: keys.crmConnection.byOrg(organizationId),
    queryFn: () => api.getConnections({ organization_id: organizationId }),
  });

  const tenants = useTenants(organizationId);

  const providers = useQuery({
    queryKey: keys.crmIntegration.providers(),
    queryFn: api.getProviders,
  });

  const mappings = useQuery({
    queryKey: keys.crmIntegration.mappings(mappingConn?.id),
    queryFn: () => api.getConnectionMappings(mappingConn!.id),
    enabled: !!mappingConn,
  });

  const pipelines = useQuery({
    queryKey: keys.crmIntegration.pipelines(mappingConn?.id),
    queryFn: () => api.getConnectionPipelines(mappingConn!.id),
    enabled: !!mappingConn,
  });

  const allMappingsQueries = useQueries({
    queries: (connections.data ?? []).map((c) => ({
      queryKey: keys.crmIntegration.mappings(c.id),
      queryFn: () => api.getConnectionMappings(c.id),
    })),
  });

  // The organization's list holds only connections mapped to its tenants, so a
  // mapping change can add or drop a connection from it.
  const invalidateConnections = () => {
    queryClient.invalidateQueries({
      queryKey: keys.crmConnection.byOrg(organizationId),
    });
  };

  const updateCredentialsMutation = useMutation({
    mutationFn: ({ id, creds }: { id: string; creds: Record<string, string> }) =>
      api.updateConnectionCredentials(id, creds),
    onSuccess: () => {
      invalidateConnections();
      setEditingConn(null);
      setNewCredentials({});
    },
  });

  const createMappingMutation = useMutation({
    mutationFn: ({
      connectionId,
      data,
    }: {
      connectionId: string;
      data: { tenant_id: string; pipeline_id?: string };
    }) => api.createConnectionMapping(connectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.crmIntegration.mappings(mappingConn?.id) });
      invalidateConnections();
      setMappingTenantId("");
      setMappingPipelineId("");
      setMappingNotice(null);
    },
  });

  const deleteMappingMutation = useMutation({
    mutationFn: ({
      connectionId,
      mappingId,
    }: {
      connectionId: string;
      mappingId: string;
    }) => api.deleteConnectionMapping(connectionId, mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.crmIntegration.mappings(mappingConn?.id) });
      invalidateConnections();
    },
  });

  const orgTenantIds = useMemo(
    () => new Set((tenants.data ?? []).map((t) => t.id)),
    [tenants.data],
  );

  const tenantsByConnection = useMemo(() => {
    const map = new Map<string, string[]>();
    const conns = connections.data ?? [];
    allMappingsQueries.forEach((q, i) => {
      if (q.data && conns[i]) {
        // Restrict displayed tenant chips to those belonging to the current org.
        const orgScopedTenantIds = [
          ...new Set(
            q.data.map((m) => m.tenant_id).filter((tid) => orgTenantIds.has(tid)),
          ),
        ];
        map.set(conns[i].id, orgScopedTenantIds);
      }
    });
    return map;
  }, [allMappingsQueries, connections.data, orgTenantIds]);

  const tenantName = (tid: string) =>
    tenants.data?.find((t) => t.id === tid)?.name ?? tid;

  const list = connections.data ?? [];
  // With a single provider the call to action names it ("Connect HubSpot").
  const onlyProvider = providers.data?.length === 1 ? providers.data[0] : null;
  const connectLabel = onlyProvider ? `Connect ${onlyProvider.name}` : "Connect CRM";

  const needAttention = list.filter(
    (c) => c.health.status === "degraded" || c.status !== "active",
  ).length;
  let status: Status;
  if (connections.isLoading) {
    status = { kind: "loading" };
  } else if (list.length === 0) {
    status = { kind: "off", label: "Not connected" };
  } else if (needAttention > 0) {
    status = {
      kind: "attention",
      label:
        list.length === 1 ? "Needs attention" : `${needAttention} of ${list.length} need attention`,
    };
  } else {
    status = { kind: "ok", label: list.length === 1 ? "Connected" : `${list.length} connected` };
  }

  const providerIds = [...new Set(list.map((c) => c.provider))];
  const providerName = (id: string) => providers.data?.find((p) => p.id === id)?.name ?? id;
  const tenantCount = new Set([...tenantsByConnection.values()].flat()).size;
  const summary =
    list.length === 0
      ? "Sync companies, contacts and deals from your CRM."
      : [
          providerIds.map(providerName).join(", "),
          ...(tenantCount ? [`${tenantCount} tenant${tenantCount === 1 ? "" : "s"}`] : []),
        ].join(" · ");
  const logoProvider =
    list.length === 0 ? (onlyProvider?.id ?? "") : providerIds.length === 1 ? providerIds[0] : "";

  return (
    <>
      <AccordionCard
        logo={<CrmProviderLogo provider={logoProvider} />}
        title="CRM"
        summary={summary}
        status={status}
        defaultExpanded={needAttention > 0 || !!mappingNotice}
      >
        {list.length === 0 ? (
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Connect {onlyProvider?.name ?? "a CRM"} to start syncing it into a tenant.
            </Typography>
            {!connections.isLoading && (
              <Button variant="contained" onClick={() => setAdding(true)}>
                {connectLabel}
              </Button>
            )}
          </Stack>
        ) : (
          <>
            <CrmConnectionList
              connections={list}
              providers={providers.data}
              tenantsByConnection={tenantsByConnection}
              tenantName={tenantName}
              onOpenMappings={(c) => setMappingConn(c)}
              onOpenPeople={(c) => setPeopleConn(c)}
              onOpenCredentials={(c) => {
                setEditingConn(c);
                setNewCredentials({});
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Plus fontSize="small" />}
              onClick={() => setAdding(true)}
              sx={{ mt: 1 }}
            >
              Add connection
            </Button>
          </>
        )}
      </AccordionCard>

      {adding && (
        <AddCrmConnectionDialog
          providers={providers.data}
          tenants={tenants.data}
          onClose={() => setAdding(false)}
          onCreated={(conn, mappingError) => {
            setAdding(false);
            invalidateConnections();
            if (mappingError) {
              setMappingNotice(
                `The connection was created but could not be mapped to the tenant (${mappingError}). Map it here, or it won't show in this organization.`,
              );
              setMappingConn(conn);
            }
          }}
        />
      )}
      <UpdateCredentialsDialog
        connection={editingConn}
        providers={providers.data}
        newCredentials={newCredentials}
        onCredentialChange={(name, value) =>
          setNewCredentials((prev) => ({ ...prev, [name]: value }))
        }
        onSave={() => {
          if (editingConn) {
            updateCredentialsMutation.mutate({
              id: editingConn.id,
              creds: newCredentials,
            });
          }
        }}
        onClose={() => setEditingConn(null)}
        saving={updateCredentialsMutation.isPending}
      />
      <CrmPeopleDialog
        connection={peopleConn}
        tenants={tenants.data}
        tenantName={tenantName}
        onClose={() => setPeopleConn(null)}
      />
      <TenantMappingsDialog
        connection={mappingConn}
        notice={mappingNotice}
        mappings={mappings.data}
        mappingsLoading={mappings.isLoading}
        pipelines={pipelines.data}
        tenants={tenants.data}
        orgTenantIds={orgTenantIds}
        tenantName={tenantName}
        mappingTenantId={mappingTenantId}
        onMappingTenantChange={setMappingTenantId}
        mappingPipelineId={mappingPipelineId}
        onMappingPipelineChange={setMappingPipelineId}
        onAddMapping={() => {
          if (!mappingConn || !mappingTenantId) return;
          createMappingMutation.mutate({
            connectionId: mappingConn.id,
            data: {
              tenant_id: mappingTenantId,
              ...(mappingPipelineId ? { pipeline_id: mappingPipelineId } : {}),
            },
          });
        }}
        onDeleteMapping={(mappingId) =>
          deleteMappingMutation.mutate({
            connectionId: mappingConn!.id,
            mappingId,
          })
        }
        addPending={createMappingMutation.isPending}
        deletePending={deleteMappingMutation.isPending}
        onClose={() => {
          setMappingConn(null);
          setMappingNotice(null);
        }}
      />
    </>
  );
}
